




















































































































import { PropType } from 'vue';
import {
  Employee,
  EmployeeTimeOffBookingBalance,
  EmployeeTimeOffEntitlement,
  ICalendarHoliday,
  IEmployeeData,
  IEmployeeTimeOffBookingBalance,
  IEmployeeTimeOffBookingBalanceData,
  IEmployeeWorkSchedule,
  ITimeOffRequest,
  ITimeOffTypeData,
  ITimeOffUnitData,
  TimeOffRequestAmountTypes,
  TimeOffStateEnum,
  TimeOffUnitEnum
} from '@/models';
import { Frequency, RRule, RRuleSet } from 'rrule';

import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

import mixins from '@/utils/mixins';
import Validatable from '@/mixins/validatable';
import BindsAttrs from '@/mixins/binds-attrs';
import auth from '@/mixins/auth';
import { PropValidator } from 'vue/types/options';
import { InputValidationRules } from 'vuetify';

const baseMixins = mixins(BindsAttrs, Validatable, auth);

interface Options extends InstanceType<typeof baseMixins> {
  /* eslint-disable-next-line camelcase */
  $_modelEvent: string;
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    name: 'TimeOffBookingBalanceCardDetail',

    props: {
      employeeBookingBalance: Object as PropType<
        IEmployeeTimeOffBookingBalanceData
      >,

      timeOff: Object as PropType<ITimeOffRequest>,

      employeeWorkSchedule: {
        type: Object as PropType<IEmployeeWorkSchedule>,
        required: false
      },

      isEdit: {
        type: Boolean,
        required: false
      },

      requestedBalance: {
        type: Number,
        required: false
      },

      disabled: {
        type: Boolean,
        required: false
      }
    },

    data() {
      return {
        loading: false,
        employee: null as Employee | null,
        localEmployeeBookingBalance: this.employeeBookingBalance,
        hasColor: true
      };
    },

    computed: {
      ...mapGetters({
        getCalendarHolidays: 'timeOffCalendar/getCalendarHolidays',
        employeeTimeOffBalance: 'employeeTimeOff/getEmployeeTimeOffBalances'
      }),

      shouldValidate() {
        if (this.readonly) return true;
        if (this.externalError) return true;
        if (this.isResetting) return false;

        return this.validateOnBlur
          ? this.hasFocused && !this.isFocused
          : this.hasInput || this.hasFocused;
      },

      calendarHolidays(): ICalendarHoliday[] {
        return this.getCalendarHolidays(this.timeOff.calendar);
      },

      bookingTotal(): number {
        if (
          (this.isEdit || this.readonly) &&
          this.timeOff.state != TimeOffStateEnum.Cancelled &&
          this.timeOff.state != TimeOffStateEnum.Rejected
        ) {
          return ((this.localEmployeeBookingBalance
            .remaining_balance as number) +
            (this.requestedBalance ?? 0)) as number;
        }

        return this.localEmployeeBookingBalance.remaining_balance as number;
      },

      bookingAmount(): number {
        if (!this.localEmployeeBookingBalance.id) return 0;

        if (this.readonly)
          return (this.timeOff.amount_requested as number) ?? 0;

        const rruleBalance = new RRuleSet();

        const dtStart = moment(this.timeOff.start!)
          .utc(true)
          .startOf('day')
          .toDate();

        const dtEnd = moment(this.timeOff.end!)
          .utc(true)
          .endOf('day')
          .toDate();

        rruleBalance.rrule(
          new RRule({
            dtstart: dtStart,
            until: dtEnd,
            byweekday: this.employeeWorkSchedule.rruleScheduledDays,
            freq: Frequency.WEEKLY
          })
        );

        this.timeOff.rule_string = rruleBalance.toString();

        this.calendarHolidays
          .filter(holiday => holiday.is_paid)
          .forEach(holiday => {
            const holidayDate = moment(holiday.date!)
              .utc()
              .startOf('day')
              .toDate();
            if (moment(holidayDate).isBetween(dtStart, dtEnd, 'day', '[]')) {
              rruleBalance.exdate(holidayDate);
            }
          });

        switch (this.timeOff.type_requested) {
          case TimeOffRequestAmountTypes.FullDay:
            this.timeOff.amount_requested = rruleBalance.count();

            if (
              (this.localEmployeeBookingBalance.employee_entitlement
                ?.timeoff_unit as ITimeOffUnitData).value ==
              TimeOffUnitEnum.Hours
            ) {
              if (!this.employeeWorkSchedule) {
                this.genEmployeeWorkScheduleInfo();
              }

              this.timeOff.amount_requested =
                rruleBalance.count() * this.employeeWorkSchedule.workday_hours!;
            }

            break;

          case TimeOffRequestAmountTypes.Partial: {
            if (
              (this.localEmployeeBookingBalance.employee_entitlement
                ?.timeoff_unit as ITimeOffUnitData).value ==
              TimeOffUnitEnum.Hours
            ) {
              this.timeOff.amount_requested =
                rruleBalance.count() *
                this.employeeWorkSchedule.workday_hours! *
                0.5;
            } else {
              this.timeOff.amount_requested = rruleBalance.count() * 0.5;
            }
            break;
          }

          case TimeOffRequestAmountTypes.Custom:
            {
              //rruleBalance.count() == 0 checks if the selected date falls on an off that for the employee
              const delta =
                rruleBalance.count() == 0
                  ? 0
                  : moment(this.timeOff.end).diff(
                      moment(this.timeOff.start),
                      'minutes'
                    ) / 60;

              if (
                (this.localEmployeeBookingBalance.employee_entitlement!
                  .timeoff_unit as ITimeOffUnitData).value ==
                TimeOffUnitEnum.Days
              ) {
                const days_requested =
                  delta / this.employeeWorkSchedule.workday_hours!;

                this.timeOff.amount_requested = days_requested;
              } else {
                this.timeOff.amount_requested = delta;
              }
            }
            break;

          default:
            break;
        }

        return this.timeOff.amount_requested!;
      },

      remainingBalance(): number {
        return this.bookingTotal - (this.bookingAmount ?? 0);
      },

      internalValue(): number {
        // For initialization of component when localEmployeeBookingBalance is not set.
        // internalValue is referenced in beforeMount
        if (!this.localEmployeeBookingBalance) return 0;
        return this.remainingBalance;
      }
    },

    watch: {
      employeeBookingBalance: {
        async handler(val) {
          if (val) {
            this.localEmployeeBookingBalance = val;
          } else {
            this.localEmployeeBookingBalance = new EmployeeTimeOffBookingBalance(
              { employee_entitlement: new EmployeeTimeOffEntitlement() }
            );
          }
        },
        immediate: true,
        deep: true
      },

      timeOff: {
        async handler(val: ITimeOffRequest) {
          if (this.readonly) {
            const employee = await this.genEmployeeBookingBalance(
              (val.employee as IEmployeeData).id as number,
              val.entitlement_year
            );
            this.localEmployeeBookingBalance = employee
              ? employee
              : new EmployeeTimeOffBookingBalance();
          }
        },
        immediate: true
      }
    },

    methods: {
      ...mapActions({
        fetchEmployeeWorkSchedule: 'employee/fetchEmployeeWorkSchedule',
        fetchEmployeeTimeOffBalance:
          'employeeTimeOff/fetchEmployeeTimeOffBalance'
      }),
      moment,

      async genEmployeeWorkScheduleInfo() {
        this.loading = true;
        await this.fetchEmployeeWorkSchedule(this.timeOff.employee);
        this.loading = false;
      },

      async genEmployeeBookingBalance(
        employeeId: number,
        year: number
      ): Promise<IEmployeeTimeOffBookingBalance | undefined> {
        const employeeBookingBalanceList: IEmployeeTimeOffBookingBalance[] = await this.fetchEmployeeTimeOffBalance(
          { employee: employeeId, year: year }
        );

        return employeeBookingBalanceList.find(
          (data: IEmployeeTimeOffBookingBalance) =>
            (data.employee_entitlement?.timeoff as ITimeOffTypeData)!.id ==
            (this.timeOff.type! as ITimeOffTypeData).id
        );
      },

      /** @public
       * override reset as this component does not function exactly as an input
       */
      reset() {
        this.isResetting = true;
      }
    }
  });
